import { createInertiaApp, router } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createApp, h } from 'vue';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import '../css/style.css';
// Both specifiers must be 'preline/dist'. The package declares no `exports` map
// and `module: "./src/index.ts"`, so a bare 'preline' import resolves to the raw
// TypeScript source — a second copy of every plugin, with its own `load`
// listeners and its own class identities. 'preline/dist' is the prebuilt bundle
// that also sets the `window.HSStaticMethods` used below.
import 'preline/dist';
import { HSOverlay } from 'preline/dist';
import { initializeTheme } from './composables/useAppearance';
import { setGoogleMapsKey } from './composables/useGoogleMaps';
import { setInstanceLocale } from './helpers';
import { useLayout } from './stores/layout';

// Fallback only. The real name is the operator's, resolved server side by
// BrandingService and shared as `branding.appName`.
const fallbackAppName = import.meta.env.VITE_APP_NAME || 'Laravel';

let appName = fallbackAppName;

createInertiaApp({
    title: (title) => (title ? `${title} - ${appName}` : appName),
    resolve: (name) =>
        resolvePageComponent(
            `./pages/${name}.vue`,
            import.meta.glob('./pages/**/*.vue'),
        ),
    setup({ el, App, props, plugin }) {
        applyInstanceProps(props.initialPage?.props);

        const pinia = createPinia();
        pinia.use(piniaPluginPersistedstate);

        createApp({ render: () => h(App, props) })
            .use(plugin)
            .use(pinia)
            .mount(el);
    },
    progress: {
        color: '#4B5563',
    },
});

/**
 * Copy the instance-wide shared props into the module-level stores that plain
 * functions read — the document title used by Inertia's `title` callback, and
 * the currency/timezone used by the helpers. Both are called from outside
 * component setup, so they cannot reach usePage().
 */
function applyInstanceProps(pageProps) {
    if (!pageProps) return;

    appName = pageProps.branding?.appName || fallbackAppName;
    setInstanceLocale(pageProps.settings?.locale);
    setGoogleMapsKey(pageProps.settings?.integrations?.googleMapsKey);
}

// Keep them current when an operator saves the settings page and navigates.
router.on('navigate', (event) => {
    applyInstanceProps(event.detail?.page?.props);
});

// This will set light / dark mode on page load...
initializeTheme();

// Clean up Preline components before Inertia navigation to prevent stale event listeners
router.on('before', () => {
    useLayout().closeMobileMenu();

    // Close any open modals
    // Guard on the id: HSOverlay.close('#') would throw a SyntaxError out of
    // querySelector and abort the rest of this handler, leaving the body locked.
    document.querySelectorAll('.hs-overlay').forEach((modal) => {
        if (modal.id && !modal.classList.contains('hidden')) {
            HSOverlay.close(`#${modal.id}`);
        }
    });

    // Safety net: ensure body scroll is restored
    document.body.classList.remove('overflow-hidden');

    // Remove any stale backdrop elements
    document.querySelectorAll('.hs-overlay-backdrop').forEach((el) => el.remove());
});

// Reinitialize Preline UI components after each Inertia page navigation
router.on('navigate', () => {
    setTimeout(() => {
        if (window.HSStaticMethods?.autoInit) {
            window.HSStaticMethods.autoInit();
        }
    }, 0);
});
